import React, { useEffect, useState } from 'react'
import { Col, Row, Button } from 'react-bootstrap'
import { Switch } from 'antd'
import { faGear } from '@fortawesome/pro-duotone-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import zoomLogo from 'assets/zoom.png'
import senceLogo from 'assets/sence.png'
import courseraLogo from 'assets/coursera.svg'
import { IntegrationTypes } from 'models/integrations'
import { toggleIntegration } from 'api/integrations/toggle'
import { Actions } from 'store/actions/general.action'
import useIntegrations from 'hooks/useIntegrations'
import Wrapper from 'components/reusables/wrapper'
import styles from './IntegrationsConfig.module.scss'
import ModalSence from './components/ModalSence'
import ModalZoom from './components/ModalZoom'
import ModalCoursera from './components/ModalCoursera'
import { Description, Title } from './styled'

const IntegrationsConfig: React.FC = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const [showSenceModal, setShowSenceModal] = useState(false)
  const [showZoomModal, setShowZoomModal] = useState(false)
  const [showCourseraModal, setShowCourseraModal] = useState(false)
  const { integrationZoom, integrationSence, integrationCoursera } =
    useIntegrations()

  const [zoomCheck, setZoomCheck] = useState(false)
  const [senceCheck, setSenceCheck] = useState(false)
  const [courseraCheck, setCourseraCheck] = useState(false)

  useEffect(() => {
    setZoomCheck(integrationZoom?.active || false)
    setSenceCheck(integrationSence?.active || false)
    setCourseraCheck(integrationCoursera?.active || false)
  }, [integrationSence?.active, integrationZoom, integrationCoursera])

  const updateIntegration = () => {
    dispatch(Actions.getIntegrationsList())
  }

  useEffect(() => {
    dispatch(Actions.getIntegrationsList())
  }, [dispatch])

  return (
    <Wrapper shadow={true}>
      <Title>{t('configuration.integrations.title')}</Title>
      <Description>{t('configuration.integrations.description')}</Description>

      {integrationCoursera && (
        <div className={styles.integration}>
          <Row>
            <Col
              xs={0.8}
              className="d-flex justify-content-center align-items-center"
            >
              <img src={courseraLogo} className="pr-0 pl-2" />
            </Col>
            <Col className="py-0">
              <div>
                <span className={styles.integrationTitle}>
                  {t('configuration.integrations.coursera')}
                </span>
                <span
                  className={
                    courseraCheck
                      ? styles.integrationActive
                      : styles.integrationInactive
                  }
                >
                  {courseraCheck
                    ? t('configuration.integrations.integrationActive')
                    : t('configuration.integrations.integrationInactive')}
                </span>
              </div>
              <span className={styles.integrationDescription}>
                {t('configuration.integrations.courseraDescription')}
              </span>
            </Col>
            <Col className="d-flex justify-content-end align-items-center">
              <Button
                variant="light"
                disabled={!courseraCheck}
                onClick={() => setShowCourseraModal(true)}
              >
                <FontAwesomeIcon icon={faGear} color="#2684FF" />
              </Button>
            </Col>
            <Col
              className="d-flex justify-content-start align-items-center"
              xs={1}
            >
              <Switch
                onClick={async (value) => {
                  const oldCheck = courseraCheck
                  setCourseraCheck(value)
                  toggleIntegration(IntegrationTypes.coursera, value).catch(
                    () => {
                      setCourseraCheck(oldCheck)
                    }
                  )
                }}
                checked={courseraCheck}
                checkedChildren={t('configuration.integrations.labelCheckYes')}
                unCheckedChildren={t('configuration.integrations.labelCheckNo')}
              />
            </Col>
          </Row>
        </div>
      )}

      {integrationZoom && (
        <div className={styles.integration}>
          <Row>
            <Col
              xs={0.8}
              className="d-flex justify-content-center align-items-center"
            >
              <img src={zoomLogo} className="pr-0 pl-2" />
            </Col>
            <Col className="py-0">
              <div>
                <span className={styles.integrationTitle}>
                  {t('configuration.integrations.zoom')}
                </span>
                <span
                  className={
                    zoomCheck
                      ? styles.integrationActive
                      : styles.integrationInactive
                  }
                >
                  {zoomCheck
                    ? t('configuration.integrations.integrationActive')
                    : t('configuration.integrations.integrationInactive')}
                </span>
              </div>
              <span className={styles.integrationDescription}>
                {t('configuration.integrations.zoomDescription')}
              </span>
            </Col>
            <Col className="d-flex justify-content-end align-items-center">
              <Button
                variant="light"
                disabled={!zoomCheck}
                onClick={() => setShowZoomModal(true)}
              >
                <FontAwesomeIcon icon={faGear} color="#2684FF" />
              </Button>
            </Col>
            <Col
              className="d-flex justify-content-start align-items-center"
              xs={1}
            >
              <Switch
                onClick={async (value) => {
                  const oldCheck = zoomCheck
                  setZoomCheck(value)
                  toggleIntegration(IntegrationTypes.zoom, value).catch(() => {
                    setZoomCheck(oldCheck)
                  })
                }}
                checked={zoomCheck}
                checkedChildren={t('configuration.integrations.labelCheckYes')}
                unCheckedChildren={t('configuration.integrations.labelCheckNo')}
              />
            </Col>
          </Row>
        </div>
      )}

      {integrationSence && (
        <div className={styles.integration}>
          <Row>
            <Col
              xs={0.8}
              className="d-flex justify-content-center align-items-center"
            >
              <img src={senceLogo} className="pr-0 pl-2" />
            </Col>
            <Col className="py-0">
              <div>
                <span className={styles.integrationTitle}>
                  {t('configuration.integrations.sence')}
                </span>
                <span
                  className={
                    senceCheck
                      ? styles.integrationActive
                      : styles.integrationInactive
                  }
                >
                  {senceCheck
                    ? t('configuration.integrations.integrationActive')
                    : t('configuration.integrations.integrationInactive')}
                </span>
              </div>
              <span className={styles.integrationDescription}>
                {t('configuration.integrations.senceDescription')}
              </span>
            </Col>
            <Col className="d-flex justify-content-end align-items-center">
              <Button
                variant="light"
                onClick={() => setShowSenceModal(true)}
                disabled={!senceCheck}
              >
                <FontAwesomeIcon icon={faGear} color="#2684FF" />
              </Button>
            </Col>
            <Col
              className="d-flex justify-content-start align-items-center"
              xs={1}
            >
              <Switch
                onClick={async (value) => {
                  const oldCheckSence = senceCheck
                  setSenceCheck(value)
                  toggleIntegration(IntegrationTypes.sence, value).catch(() => {
                    setSenceCheck(oldCheckSence)
                  })
                }}
                checked={senceCheck}
                checkedChildren={t('configuration.integrations.labelCheckYes')}
                unCheckedChildren={t('configuration.integrations.labelCheckNo')}
              />
            </Col>
          </Row>
        </div>
      )}
      {showSenceModal && (
        <ModalSence
          show={showSenceModal}
          onClose={() => setShowSenceModal(false)}
        />
      )}
      {showZoomModal && (
        <ModalZoom
          show={showZoomModal}
          onClose={() => setShowZoomModal(false)}
          integration={integrationZoom}
          updateIntegration={updateIntegration}
        />
      )}
      {showCourseraModal && (
        <ModalCoursera
          show={showCourseraModal}
          onClose={() => setShowCourseraModal(false)}
          integration={integrationCoursera}
          updateIntegration={updateIntegration}
        />
      )}
    </Wrapper>
  )
}

export default IntegrationsConfig
